/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useRef } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import { generateWallet } from '../../utils';

import CopyButton from '../common/CopyButton';
import TooltipButton from '../common/TooltipButton';
import FieldWrapper from '../form/wrappers/Field';

function AddressFieldWrapper({
  wallet,
  autoFocus,
  label,
  readOnly,
  onAddressChange,
  onGenerate,
  showDesc,
}) {
  const addressRef = useRef(null);
  const { t } = useTranslation();

  const generate = () => {
    generateWallet((newWallet) => {
      if (onGenerate) onGenerate(newWallet);
    });
  };

  useEffect(() => {
    if (autoFocus && addressRef.current)
      setTimeout(() => {
        addressRef.current.focus();
      }, 100);
  }, [addressRef]);

  useEffect(() => {
    if (!wallet.privateKey) generate();
  }, []);

  return (
    <Field
      required
      className="blockchain_address"
      tabIndex={0}
      readOnly
      id={`keyGenForm.${uuid()}.blockchain_address`}
      label={label || t('applications.detail.form.fields.address.label')}
      placeholder={t('applications.detail.form.fields.address.placeholder')}
      note={!readOnly && showDesc && t('applications.detail.form.fields.address.description')}
      handleChange={onAddressChange}
      append={
        <div className="d-flex align-items-center">
          {wallet.blockchain_address && document.queryCommandSupported('copy') && (
            <CopyButton inputRef={addressRef} variant="link" placement="top" className="p-1 ms-2" />
          )}
          {!readOnly && (
            <Button
              className="generate ms-2 rounded-1"
              variant="primary"
              size="sm"
              onClick={() => generate()}
            >
              {t('common:buttons.generate')}
            </Button>
          )}
        </div>
      }
      name="blockchain_address"
      inputRef={addressRef}
      component={FieldWrapper}
      floating={false}
    />
  );
}

AddressFieldWrapper.propTypes = {
  /**
   * The wallet values
   */
  wallet: PropTypes.object,
  /**
   * The autofocus boolean
   */
  autoFocus: PropTypes.bool,
  /**
   * The optional label
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /**
   * The read only boolean
   */
  readOnly: PropTypes.bool,
  /**
   * The function that is called when the address field value has changed.
   */
  onAddressChange: PropTypes.func,
  /**
   * The function that is called after the address is autogenerated
   */
  onGenerate: PropTypes.func,
  /**
   * The show description boolean
   */
  showDesc: PropTypes.bool,
};

function PrivateKeyFieldWrapper({ label, wallet, show }) {
  const [reveal, setReveal] = useState(show);
  const privateKeyRef = useRef(null);
  const { t } = useTranslation();
  return (
    <Form.Group
      className="privateKey loaded readonly mt-3"
      controlId={`keyGenForm.${uuid()}.privateKey`}
    >
      <Form.Label>{label || t('applications.detail.form.fields.key.label')}</Form.Label>
      <InputGroup>
        <Form.Control
          autoComplete="new-password"
          type={reveal ? 'text' : 'password'}
          name="privateKey"
          value={wallet.privateKey}
          readOnly
        />
        <input
          className="visually-hidden"
          type="text"
          name="privateKey-hidden"
          value={wallet.privateKey}
          ref={privateKeyRef}
          readOnly
        />
        <TooltipButton
          placement="top"
          variant="link"
          text={reveal ? t('common:buttons.hide') : t('common:buttons.reveal')}
          popoverProps={{ style: { textAlign: 'center' } }}
          onClick={() => setReveal(!reveal)}
          className="p-1"
        >
          <i className={`text-lg fas fa-${reveal ? 'eye-slash' : 'eye'}`} />
        </TooltipButton>
        {document.queryCommandSupported('copy') && (
          <CopyButton
            inputRef={privateKeyRef}
            variant="link"
            placement="top"
            className="p-1 ms-2"
          />
        )}
      </InputGroup>
    </Form.Group>
  );
}

PrivateKeyFieldWrapper.propTypes = {
  /**
   * The show boolean to reveal the private key
   */
  show: PropTypes.bool,
  /**
   * The wallet values
   */
  wallet: PropTypes.object,
  /**
   * The optional label
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

function KeyGenForm({ wallet, onAddressChange, onGenerate, readOnly, show }) {
  const [reveal, setReveal] = useState(show);

  const handleGenerate = (newWallet) => {
    if (onGenerate) onGenerate(newWallet);
    if (!reveal) setReveal(true);
  };

  return (
    <>
      <AddressFieldWrapper
        autoFocus
        showDesc
        readOnly={readOnly}
        wallet={wallet}
        onGenerate={handleGenerate}
        onAddressChange={onAddressChange}
      />
      {wallet.privateKey && <PrivateKeyFieldWrapper show={reveal} wallet={wallet} />}
    </>
  );
}

KeyGenForm.propTypes = {
  /**
   * Wallet values
   */
  wallet: PropTypes.object,
  /**
   * The function that is called when the address field value has changed.
   */
  onAddressChange: PropTypes.func,
  /**
   * The function that is called when the address is generated.
   */
  onGenerate: PropTypes.func,
  /**
   * The read only boolean
   */
  readOnly: PropTypes.bool,
  /**
   * The show boolean to reveal the private key
   */
  show: PropTypes.bool,
};

export default KeyGenForm;

export const AddressField = AddressFieldWrapper;
export const PrivateKeyField = PrivateKeyFieldWrapper;
